import { useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'

import { logoutUser } from '@/actions/authActions'
import { selectIsReactivatedUser } from '@/selectors/user.selectors'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsReset } from '@/features/amplitude'

import {
  SIGNUP_STEPS,
  SIGNUP_STEPS_TAXES,
  SIGNUP_STEP_MAP,
  SIGNUP_PATHS,
  isEligibleTrialCode,
} from './helpers'

import { Alert, Button, Image, Progress } from '@/ui'
import { selectCanOptIntoPreviousTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useChooserShown } from './plans/chooserVisibility'

const SignupHeaderLogic = () => {
  const location = useLocation()
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const chooserShown = useChooserShown()

  const progress = useMemo(() => {
    let steps = [
      'Get Started',
      ...(canOptIntoPreviousYearTaxes ? SIGNUP_STEPS_TAXES : SIGNUP_STEPS),
    ]

    if (!chooserShown) {
      steps = steps.filter((step) => step !== 'Choose Plan')
    }

    const pathname = location.pathname
    const currentStepName =
      SIGNUP_STEP_MAP[pathname as keyof typeof SIGNUP_STEP_MAP]
    let currentStep = currentStepName
      ? steps.findIndex((step) => step === currentStepName)
      : 0

    // Handle edge case: if user is on "Choose Plan" page but feature flag filters it out,
    // show them as being on the "Your Practice" step instead
    if (currentStep === -1 && pathname === SIGNUP_PATHS.choosePlan) {
      currentStep = steps.findIndex((step) => step === 'Your Practice')
    }

    const totalSteps = steps.length

    if (isMobile) {
      return (
        <Progress
          variant="segmented"
          numberOfSegments={totalSteps}
          activeSegment={currentStep + 1}
        />
      )
    }

    return (
      <Progress
        variant="segmented"
        numberOfSegments={steps.length}
        activeSegment={currentStep + 1}
        segmentTitles={steps}
      />
    )
  }, [location.pathname, canOptIntoPreviousYearTaxes, chooserShown, isMobile])

  return progress
}

const DesktopHeader = ({
  loggedIn,
  handleLogoutClick,
  reactivating,
  hideProgress,
}: {
  loggedIn: boolean
  handleLogoutClick: () => void
  reactivating: boolean
  hideProgress?: boolean
}) => {
  return (
    <>
      <div className="flex items-center justify-between pb-0">
        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
          alt="heard logo"
          width={75}
        />

        {loggedIn && (
          <Button
            className="text-brand"
            variant="link"
            onClick={() => handleLogoutClick()}
          >
            Logout
          </Button>
        )}
      </div>

      {!hideProgress && (
        <div className="min-h-fit mt-6">
          <SignupHeaderLogic />
        </div>
      )}

      {reactivating && (
        <Alert
          className="min-h-fit mt-4"
          title="Welcome back to Heard!"
          description="To reactivate your account, please proceed through the sign up flow."
        />
      )}
    </>
  )
}

const MobileHeader = ({
  loggedIn,
  handleLogoutClick,
  hideProgress,
}: {
  loggedIn: boolean
  handleLogoutClick: () => void
  hideProgress?: boolean
}) => {
  return (
    <>
      <div className="flex items-center justify-between pb-0">
        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
          alt="heard logo"
          className="h-5"
        />

        {loggedIn && (
          <Button
            className="float-right"
            variant="link"
            onClick={() => handleLogoutClick()}
          >
            Logout
          </Button>
        )}
      </div>

      {!hideProgress && (
        <div className="min-h-fit mt-6">
          <div className="sticky top-0 bg-white shadow-md -mx-14 px-14 z-10">
            <SignupHeaderLogic />
          </div>
        </div>
      )}
    </>
  )
}

export const SignupHeader = ({ hideProgress }: { hideProgress?: boolean }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const resetAnalytics = useAnalyticsReset()
  const reactivatingUser = useReselector(selectIsReactivatedUser)

  // Only strips codes we no longer honor. Stripping a live code silently
  // disables the trial it names, with no error and no analytics signal.
  useEffect(() => {
    const freeTrialParam = searchParams.get('trial')

    if (freeTrialParam && !isEligibleTrialCode(freeTrialParam)) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.delete('trial')
          return newParams
        },
        { replace: true }
      )
    }
  }, [searchParams, setSearchParams])

  // Steps that run before an account exists, so there is nothing to log out of.
  const loggedIn = ![
    SIGNUP_PATHS.emailCapture,
    SIGNUP_PATHS.aboutYou,
    SIGNUP_PATHS.readinessAccount,
  ].includes(location.pathname)

  const handleLogoutClick = useCallback(async () => {
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    navigate('/login')
  }, [resetAnalytics, navigate, dispatch])

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return isMobile ? (
    <MobileHeader
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      hideProgress={hideProgress}
    />
  ) : (
    <DesktopHeader
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      reactivating={reactivatingUser}
      hideProgress={hideProgress}
    />
  )
}
